/* eslint-disable @typescript-eslint/member-ordering */
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Output, EventEmitter, ElementRef, HostBinding, HostListener } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IProvider } from '@app/models/provider';
import { OrchestratorService } from '@app/services/orchestrator.service';
import { DeleteReason } from '@models/delete-reason';
import { firstValueFrom } from 'rxjs';

@Component({
	selector: 'mv-edit-vet',
	templateUrl: './edit-vet.component.html',
	styleUrls: ['./edit-vet.component.scss'],
	animations: [
		trigger('state', [
			state('open', style({ opacity: '1', pointerEvents: 'all' })),
			state('void, closed', style({ opacity: '0', pointerEvents: 'none' })),
			transition('* => *', animate('155ms ease-in')),
		]),
	],
})
export class EditVetComponent {
	public petName?: string;
	public editForm: FormGroup;
	public questionaireControl: FormControl = new FormControl(null, Validators.required);
	public showQuestionaire: boolean = false;
	public DeleteReason = DeleteReason;
	public submissionAttempted: boolean = false;
	private readonly maxInputLength: number = 100;

	@HostBinding('attr.data-emb-element') embAttribute = true;
	@HostBinding('@state') state: 'open' | 'closed' = 'closed';

	@HostListener('window:emb_mv:global_edit_vet', ['$event'])
	@HostListener('emb_mv_open_edit', ['$event'])
	handleOpenEdit(ev: CustomEvent): void {
		const { petId, clinicId } = ev.detail;
		this.clinicId = clinicId;
		this.petId = petId;
		this.populateClinicInfo(<string>petId, <string>clinicId)
			.then(() => this._open())
			.catch(err => console.error(err));
	}

	private petId: string = '';
	private clinicId: string = '';
	@Output() emb_mv_close: EventEmitter<void> = new EventEmitter<void>();

	constructor(
		public readonly el: ElementRef<HTMLElement>,
		private readonly fb: FormBuilder,
		private readonly orchestratorService: OrchestratorService
	) {
		this.editForm = this.fb.group({
			vetClinic: [null],
			otherPetNames: [null, Validators.maxLength(this.maxInputLength)],
			otherOwnerNames: [null, Validators.maxLength(this.maxInputLength)],
		});

		this.vetClinicControl?.disable();
	}

	private get vetClinicControl(): AbstractControl | null {
		return this.editForm.get('vetClinic');
	}

	private get otherPetNamesControl(): AbstractControl | null {
		return this.editForm.get('otherPetNames');
	}

	private get otherOwnerNamesControl(): AbstractControl | null {
		return this.editForm.get('otherOwnerNames');
	}

	public delete(): void {
		this.showQuestionaire = true;
	}

	public async confirmDelete(): Promise<void> {
		this.submissionAttempted = true;
		if (!this.petId || !this.clinicId || this.questionaireControl.invalid) return;
		try {
			await firstValueFrom(
				this.orchestratorService.deleteVet({
					petId: this.petId,
					vetId: this.clinicId,
					reason: this.questionaireControl.value,
				})
			);
			this.close(true);
		} catch (err) {
			Promise.reject(err);
		}
	}

	public async save(): Promise<void> {
		if (this.editForm.pristine || this.editForm.untouched || !this.petId || !this.clinicId) return;
		try {
			await firstValueFrom(
				this.orchestratorService.editVet(this.petId, {
					vetId: this.clinicId,
					otherNames: this.otherPetNamesControl?.value ?? '',
					otherOwners: this.otherOwnerNamesControl?.value ?? '',
				})
			);
			this.close(true);
		} catch (err) {
			Promise.reject(err);
		}
	}

	private _open(petId: string = this.petId, clinicId: string = this.clinicId): void {
		if (!petId || !clinicId) return;
		this.populateClinicInfo(petId, clinicId)
			.then(() => (this.state = 'open'))
			.catch(err => console.error(err));
	}

	public close(shouldUpdate: boolean = false): void {
		this.state = 'closed';
		this.submissionAttempted = false;
		this.editForm.reset();
		this.questionaireControl.reset();
		this.showQuestionaire = false;
		if (shouldUpdate) {
			const updateEvent: CustomEvent = new CustomEvent('emb_mv:global_update', { bubbles: true });
			this.el.nativeElement.dispatchEvent(updateEvent);
		}
	}

	private async populateClinicInfo(petId: string = this.petId, clinicId: string = this.clinicId): Promise<void> {
		if (!petId || !clinicId) return;
		try {
			const pet = await firstValueFrom(this.orchestratorService.getPetDetails(petId)).then(d => d.pet);
			const {
				name: vetClinic = '',
				otherPetNames = '',
				otherOwnerNames = '',
			} = pet.providers.find(v => v.id === clinicId) ?? <IProvider>{};
			this.editForm.patchValue({
				vetClinic,
				otherPetNames,
				otherOwnerNames,
			});
		} catch (err) {
			console.error(err);
			Promise.reject(err);
		}
	}
}
